"use client";
import type { FC, ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { signOut } from "@/Store/Actions/Auth";
import { navigation } from "@/Helpers/WorkspaceData";
import { L } from "@/libs/localization/i18n";
import { Icon } from "../icons";
import { useWorkspace } from "../workspace";

type SidebarProps = {
  profileStatus: ReactNode;
  user: { name: string; initials: string };
  open: boolean;
  onClose: () => void;
};

export const Sidebar: FC<SidebarProps> = ({
  user,
  open,
  onClose,
  profileStatus,
}) => {
  const { locale, t, l, tasks, paymentStatus, archived } = useWorkspace();
  const current = usePathname().split("/")[2] || "work-queue";
  return (
    <>
      {open && (
        <button
          className="nav-scrim hidden max-[760px]:block max-[760px]:fixed max-[760px]:inset-0 max-[760px]:bg-[#0f1b2d60] max-[760px]:z-45 max-[760px]:border-0"
          aria-label={t("close")}
          onClick={() => onClose()}
        />
      )}
      <aside
        className={`sidebar fixed inset-y-0 left-0 right-auto w-[242px] bg-ink pt-6.5 px-3.5 pb-0 text-[#adb9cd] flex flex-col z-50 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#26344c_transparent] max-[1200px]:w-[220px] max-[1200px]:pl-2.5 max-[1200px]:pr-2.5 max-[760px]:-translate-x-full max-[760px]:w-[242px] max-[760px]:transition-transform max-[760px]:duration-200 max-[760px]:[&.is-open]:translate-x-0 ${open ? "is-open" : ""}`}
      >
        <Link
          href={`/${locale}/work-queue`}
          className="brand flex items-center pt-0 px-3.5 pb-5.25 [&_img]:w-[129px] [&_img]:h-auto [&_img]:block"
          onClick={() => onClose()}
        >
          <img src="/baken-logo.svg" alt="Baken" width="132" height="38" />
        </Link>
        <div className="workspace-label flex items-center gap-2 text-[11px] text-[#b4c0d3] mt-0 mx-2.5 mb-5.5 py-2.5 px-2.25 border border-[#2a374b] rounded-[7px] [&_>_.icon]:ml-auto [&_>_.icon]:w-3 [&_>_.icon]:h-3">
          <span className="workspace-dot w-1.5 h-1.5 rounded-full bg-cyan" />
          {t("workspace")}
          <Icon name="down" />
        </div>
        <nav aria-label={t("workspace")}>
          {navigation.map((n) => (
            <div key={n.screen}>
              {n.group && (
                <div className="nav-section text-[9px] tracking-[1.5px] uppercase font-semibold text-[#8998b1] pt-5.25 px-3.25 pb-2.25">
                  {t(n.group)}
                </div>
              )}
              <Link
                href={`/${locale}/${n.screen}`}
                className={`nav-link flex items-center gap-2.75 py-2.5 px-3 rounded-lg text-[11.5px] min-h-10 mb-0.75 transition-colors duration-150 [&_.icon]:w-4.25 [&_.icon]:h-4.25 [&_>_span:nth-child(2)]:flex-1 [&:hover]:bg-[#1a2a42] [&:hover]:text-white [&.active]:bg-teal [&.active]:text-white [&.active]:shadow-[0_4px_12px_#0001] min-[1600px]:text-[12px] max-[1200px]:text-[10.5px] max-[1200px]:gap-2.25 max-[1200px]:pl-2.5 max-[1200px]:pr-2.5 ${current === n.screen ? "active" : ""}`}
                aria-current={current === n.screen ? "page" : undefined}
                onClick={() => {
                  onClose();
                }}
              >
                <Icon name={n.icon} />
                <span>{t(n.label)}</span>
                {n.screen === "work-queue" ? (
                  <span className="nav-badge danger text-[10px] leading-[1] py-1 px-1.5 rounded-[5px] min-w-5.25 text-center bg-[#29374e] text-[#d4deec] font-semibold [&.danger]:bg-[#e45963] [&.danger]:text-white [&.amber]:bg-[#7a522e] [&.amber]:text-[#ffe0b3]">
                    {tasks.length}
                  </span>
                ) : n.badge !== undefined ? (
                  <span
                    className={`nav-badge text-[10px] leading-[1] py-1 px-1.5 rounded-[5px] min-w-5.25 text-center bg-[#29374e] text-[#d4deec] font-semibold [&.danger]:bg-[#e45963] [&.danger]:text-white [&.amber]:bg-[#7a522e] [&.amber]:text-[#ffe0b3] ${n.screen === "bank" ? "amber" : ""}`}
                  >
                    {n.screen === "bank"
                      ? 6 - Object.keys(paymentStatus).length
                      : n.screen === "mailroom"
                        ? 12 - archived.length
                        : n.badge}
                  </span>
                ) : null}
              </Link>
            </div>
          ))}
        </nav>
        <div className="sidebar-bottom mt-auto pt-7.5">
          <form action={signOut} className="px-2 pb-2">
            <input type="hidden" name="locale" value={locale} />
            <button
              type="submit"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[11px] text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Icon name="logout" />
              {l(L("Uitloggen", "Sign out"))}
            </button>
          </form>
          <div className="demo-marker flex items-center gap-1.75 p-3 text-[#a2b0c6] text-[10px] [&_>_span]:w-1.25 [&_>_span]:h-1.25 [&_>_span]:bg-[#8d9bb0] [&_>_span]:rounded-full">
            <span />
            {t("demo")}
          </div>
          <Link
            className="sidebar-profile flex items-center gap-2.5 py-4.25 px-2.5 border-t border-t-[#29354a] mt-2 [&_strong]:text-[11px] [&_strong]:text-[#f2f5fa] [&_strong]:block [&_small]:text-[10px] [&_small]:block [&_small]:text-[#9dacc2] [&_>_.icon]:ml-auto [&_>_.icon]:w-3.75 [&_.avatar]:bg-[#293b51] [&_.avatar]:text-[#c6e5e8]"
            href={`/${locale}/settings`}
            onClick={onClose}
          >
            <span className="avatar w-8.5 h-8.5 rounded-[10px] inline-flex items-center justify-center flex-none bg-teal text-white text-[11px] font-semibold">
              {user.initials}
            </span>
            <span>
              <strong>{user.name}</strong>
              {profileStatus}
            </span>
            <Icon name="settings" />
          </Link>
        </div>
      </aside>
    </>
  );
};
